import React from 'react';
import './MeshtasticWeatherIconSummary.css';
import { toNumber } from './meshtasticWeatherUnits';

const MOON_SYNODIC_MONTH_DAYS = 29.53058867;
const KNOWN_NEW_MOON_MS = Date.UTC(2000, 0, 6, 18, 14, 0, 0);
const CIVIL_TWILIGHT_AFTER_SUNSET_MS = 30 * 60 * 1000;
const SUNSET_INDICATION_BEFORE_SUNSET_MS = 20 * 60 * 1000;
const PHILADELPHIA_LATITUDE_DEG = 39.9526;
const PHILADELPHIA_LONGITUDE_DEG = -75.1652;
const WEATHER_STATION_TIME_ZONE = 'America/New_York';
const OFFICIAL_SUNSET_ZENITH_DEG = 90.833;
const RAIN_INTENSITY_THRESHOLDS_MM = {
  heavy: 5,
  moderate: 1.5,
};
const RAIN_DROP_PATHS = {
  light: [
    'M36 65c3.5 5.8 3.5 5.8 0 11.5',
    'M60 65c3.5 5.8 3.5 5.8 0 11.5',
  ],
  moderate: [
    'M28 64c3.5 5.8 3.5 5.8 0 11.5',
    'M42 70c3.5 5.8 3.5 5.8 0 11.5',
    'M56 64c3.5 5.8 3.5 5.8 0 11.5',
    'M70 70c3.5 5.8 3.5 5.8 0 11.5',
  ],
  heavy: [
    'M22 62c3.5 5.8 3.5 5.8 0 11.5',
    'M34 70c3.5 5.8 3.5 5.8 0 11.5',
    'M46 62c3.5 5.8 3.5 5.8 0 11.5',
    'M58 70c3.5 5.8 3.5 5.8 0 11.5',
    'M70 62c3.5 5.8 3.5 5.8 0 11.5',
    'M82 70c3.5 5.8 3.5 5.8 0 11.5',
  ],
};
const SUNSET_FIELD_KEYS = [
  'sunset',
  'sunset_at',
  'sunset_time',
  'sunset_time_utc',
  'sunset_ts',
  'sunset_utc',
  'sunset_ms',
  'sunset_timestamp',
  'local_sunset',
  'local_sunset_time',
];
const CIVIL_TWILIGHT_END_FIELD_KEYS = [
  'civil_twilight_end',
  'civil_twilight_end_utc',
  'civil_twilight_end_time',
  'civil_twilight_end_at',
  'civil_twilight_end_timestamp',
  'civil_twilight_end_ms',
];

function parseTimestampMs(value) {
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null;
    }

    return value >= 1e11 ? value : value * 1000;
  }

  if (typeof value === 'string') {
    const normalized = value.trim();

    if (!normalized) {
      return null;
    }

    if (/^\d+(\.\d+)?$/.test(normalized)) {
      const numericValue = Number(normalized);
      return Number.isFinite(numericValue)
        ? (numericValue >= 1e11 ? numericValue : numericValue * 1000)
        : null;
    }

    const parsed = Date.parse(normalized);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
}

function normalizeDegrees(value) {
  return ((value % 360) + 360) % 360;
}

export function getRainIntensityDetails(weatherData) {
  const rainValues = [
    toNumber(weatherData?.rain_interval_mm),
    toNumber(weatherData?.nearcast_rain_mm),
  ].filter((value) => value !== null);
  const rainAmountMm = rainValues.length ? Math.max(...rainValues, 0) : 0;
  const precipitationType = toNumber(weatherData?.precipitation_type) ?? 0;

  if (rainAmountMm >= RAIN_INTENSITY_THRESHOLDS_MM.heavy) {
    return {
      dropletCount: RAIN_DROP_PATHS.heavy.length,
      key: 'heavy',
      rainAmountMm,
    };
  }

  if (rainAmountMm >= RAIN_INTENSITY_THRESHOLDS_MM.moderate) {
    return {
      dropletCount: RAIN_DROP_PATHS.moderate.length,
      key: 'moderate',
      rainAmountMm,
    };
  }

  return {
    dropletCount: RAIN_DROP_PATHS.light.length,
    key: 'light',
    rainAmountMm,
    precipitationOnly: rainAmountMm <= 0 && precipitationType > 0,
  };
}

function getTimeZoneDateParts(timestampMs, timeZone = WEATHER_STATION_TIME_ZONE) {
  const fallbackDate = new Date(timestampMs);

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(fallbackDate);
    const partMap = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return {
      year: Number(partMap.year),
      month: Number(partMap.month),
      day: Number(partMap.day),
    };
  } catch (error) {
    return {
      year: fallbackDate.getFullYear(),
      month: fallbackDate.getMonth() + 1,
      day: fallbackDate.getDate(),
    };
  }
}

function compareDateParts(left, right) {
  if (left.year !== right.year) return left.year - right.year;
  if (left.month !== right.month) return left.month - right.month;
  return left.day - right.day;
}

function getDayOfYear({ year, month, day }) {
  const startOfYear = Date.UTC(year, 0, 0);
  const currentDay = Date.UTC(year, month - 1, day);
  return Math.floor((currentDay - startOfYear) / 86400000);
}

function getCalculatedSunsetTimestampMs(timestampMs) {
  if (!Number.isFinite(timestampMs)) {
    return null;
  }

  const dateParts = getTimeZoneDateParts(timestampMs);
  const dayOfYear = getDayOfYear(dateParts);
  const longitudeHour = PHILADELPHIA_LONGITUDE_DEG / 15;
  const approximateTime = dayOfYear + ((18 - longitudeHour) / 24);
  const meanAnomaly = (0.9856 * approximateTime) - 3.289;
  const trueLongitude = normalizeDegrees(
    meanAnomaly +
    (1.916 * Math.sin(meanAnomaly * (Math.PI / 180))) +
    (0.02 * Math.sin(2 * meanAnomaly * (Math.PI / 180))) +
    282.634,
  );
  let rightAscension = Math.atan(
    0.91764 * Math.tan(trueLongitude * (Math.PI / 180)),
  ) * (180 / Math.PI);

  rightAscension = normalizeDegrees(rightAscension);
  rightAscension += Math.floor(trueLongitude / 90) * 90 - Math.floor(rightAscension / 90) * 90;
  rightAscension /= 15;

  const sinDeclination = 0.39782 * Math.sin(trueLongitude * (Math.PI / 180));
  const cosDeclination = Math.cos(Math.asin(sinDeclination));
  const latitudeRadians = PHILADELPHIA_LATITUDE_DEG * (Math.PI / 180);
  const sunsetHourCosine = (
    Math.cos(OFFICIAL_SUNSET_ZENITH_DEG * (Math.PI / 180)) -
    (sinDeclination * Math.sin(latitudeRadians))
  ) / (cosDeclination * Math.cos(latitudeRadians));

  if (sunsetHourCosine < -1 || sunsetHourCosine > 1) {
    return null;
  }

  const sunsetHour = (Math.acos(sunsetHourCosine) * (180 / Math.PI)) / 15;
  const localMeanTime = sunsetHour + rightAscension - (0.06571 * approximateTime) - 6.622;
  const utcHour = ((localMeanTime - longitudeHour) % 24 + 24) % 24;
  const utcHourWhole = Math.floor(utcHour);
  const utcMinute = Math.floor((utcHour - utcHourWhole) * 60);
  const utcSecond = Math.round((((utcHour - utcHourWhole) * 60) - utcMinute) * 60);
  let sunsetTimestampMs = Date.UTC(
    dateParts.year,
    dateParts.month - 1,
    dateParts.day,
    utcHourWhole,
    utcMinute,
    utcSecond,
  );
  const sunsetLocalDateParts = getTimeZoneDateParts(sunsetTimestampMs);
  const dateComparison = compareDateParts(sunsetLocalDateParts, dateParts);

  if (dateComparison < 0) {
    sunsetTimestampMs += 86400000;
  } else if (dateComparison > 0) {
    sunsetTimestampMs -= 86400000;
  }

  return sunsetTimestampMs;
}

function getSunsetTimestampMs(weatherData, observationTimestampMs) {
  if (weatherData && typeof weatherData === 'object') {
    for (const key of SUNSET_FIELD_KEYS) {
      const parsedTimestamp = parseTimestampMs(weatherData[key]);

      if (parsedTimestamp !== null) {
        return parsedTimestamp;
      }
    }

    const sunsetEntry = Object.entries(weatherData).find(([key]) => (
      key.toLowerCase().replace(/[^a-z]/g, '').includes('sunset')
    ));

    if (sunsetEntry) {
      const parsedTimestamp = parseTimestampMs(sunsetEntry[1]);

      if (parsedTimestamp !== null) {
        return parsedTimestamp;
      }
    }
  }

  return getCalculatedSunsetTimestampMs(observationTimestampMs);
}

function getCivilTwilightEndTimestampMs(weatherData, fallbackTimestampMs) {
  if (weatherData && typeof weatherData === 'object') {
    for (const key of CIVIL_TWILIGHT_END_FIELD_KEYS) {
      const parsedTimestamp = parseTimestampMs(weatherData[key]);

      if (parsedTimestamp !== null) {
        return parsedTimestamp;
      }
    }

    const civilTwilightEndEntry = Object.entries(weatherData).find(([key]) => (
      key.toLowerCase().replace(/[^a-z]/g, '').includes('civiltwilightend')
    ));

    if (civilTwilightEndEntry) {
      const parsedTimestamp = parseTimestampMs(civilTwilightEndEntry[1]);

      if (parsedTimestamp !== null) {
        return parsedTimestamp;
      }
    }
  }

  return fallbackTimestampMs;
}

export function getCivilTwilightDetails(weatherData, timestampValue) {
  const observationTimestampMs =
    parseTimestampMs(timestampValue) ??
    parseTimestampMs(weatherData?.timestamp) ??
    Date.now();
  const sunsetTimestampMs = getSunsetTimestampMs(weatherData, observationTimestampMs);
  const fallbackCivilTwilightEndTimestampMs = sunsetTimestampMs === null
    ? null
    : sunsetTimestampMs + CIVIL_TWILIGHT_AFTER_SUNSET_MS;
  const civilTwilightEndTimestampMs =
    getCivilTwilightEndTimestampMs(weatherData, fallbackCivilTwilightEndTimestampMs);
  const sunsetWindowStartTimestampMs = sunsetTimestampMs === null
    ? null
    : sunsetTimestampMs - SUNSET_INDICATION_BEFORE_SUNSET_MS;
  const isSunsetApproaching =
    sunsetWindowStartTimestampMs !== null &&
    sunsetTimestampMs !== null &&
    observationTimestampMs >= sunsetWindowStartTimestampMs &&
    observationTimestampMs < sunsetTimestampMs;
  const isCivilTwilightActive =
    sunsetTimestampMs !== null &&
    civilTwilightEndTimestampMs !== null &&
    observationTimestampMs >= sunsetTimestampMs &&
    observationTimestampMs < civilTwilightEndTimestampMs;
  const isBeforeSunset =
    sunsetTimestampMs !== null &&
    observationTimestampMs < sunsetTimestampMs;

  return {
    observationTimestampMs,
    sunsetTimestampMs,
    sunsetWindowStartTimestampMs,
    civilTwilightEndTimestampMs,
    isBeforeSunset,
    isSunsetApproaching,
    isCivilTwilightActive,
    isActive: isSunsetApproaching || isCivilTwilightActive,
  };
}

export function isInCivilTwilightAfterSunset(weatherData, timestampValue) {
  return getCivilTwilightDetails(weatherData, timestampValue).isCivilTwilightActive;
}

export function getMoonPhaseDetails(timestampValue = Date.now()) {
  const timestampMs = parseTimestampMs(timestampValue) ?? Date.now();
  const elapsedDays = (timestampMs - KNOWN_NEW_MOON_MS) / 86400000;
  const normalizedPhase =
    ((elapsedDays % MOON_SYNODIC_MONTH_DAYS) + MOON_SYNODIC_MONTH_DAYS) % MOON_SYNODIC_MONTH_DAYS;
  const phaseFraction = normalizedPhase / MOON_SYNODIC_MONTH_DAYS;
  const illumination = (1 - Math.cos(phaseFraction * 2 * Math.PI)) / 2;
  const waxing = phaseFraction < 0.5;

  let key = 'new-moon';
  let label = 'New Moon';

  if (phaseFraction >= 0.03125 && phaseFraction < 0.21875) {
    key = 'waxing-crescent';
    label = 'Waxing Crescent';
  } else if (phaseFraction >= 0.21875 && phaseFraction < 0.28125) {
    key = 'first-quarter';
    label = 'First Quarter';
  } else if (phaseFraction >= 0.28125 && phaseFraction < 0.46875) {
    key = 'waxing-gibbous';
    label = 'Waxing Gibbous';
  } else if (phaseFraction >= 0.46875 && phaseFraction < 0.53125) {
    key = 'full-moon';
    label = 'Full Moon';
  } else if (phaseFraction >= 0.53125 && phaseFraction < 0.71875) {
    key = 'waning-gibbous';
    label = 'Waning Gibbous';
  } else if (phaseFraction >= 0.71875 && phaseFraction < 0.78125) {
    key = 'last-quarter';
    label = 'Last Quarter';
  } else if (phaseFraction >= 0.78125 && phaseFraction < 0.96875) {
    key = 'waning-crescent';
    label = 'Waning Crescent';
  }

  return {
    key,
    label,
    waxing,
    phaseFraction,
    illumination,
  };
}

export function resolveWeatherSummary(weatherData, timestampValue) {
  const rainInterval = toNumber(weatherData?.rain_interval_mm) ?? 0;
  const nearcastRain = toNumber(weatherData?.nearcast_rain_mm) ?? 0;
  const lightningCount = toNumber(weatherData?.lightning_strike_count) ?? 0;
  const uvIndex = toNumber(weatherData?.uv_index);
  const solarRadiation = toNumber(weatherData?.solar_radiation_wm2);
  const illuminance = toNumber(weatherData?.illuminance_lux);
  const precipitationType = toNumber(weatherData?.precipitation_type) ?? 0;
  const windAverage = toNumber(weatherData?.wind_avg_ms) ?? 0;
  const civilTwilightDetails = getCivilTwilightDetails(weatherData, timestampValue);
  const isSunsetWindow = civilTwilightDetails.isActive;
  const isCivilTwilight = civilTwilightDetails.isCivilTwilightActive;
  const isBeforeSunset = civilTwilightDetails.isBeforeSunset;
  const isMoonlightRange = illuminance !== null && illuminance <= 5;
  const isNightFromSolarAndUv =
    solarRadiation !== null &&
    solarRadiation <= 10 &&
    uvIndex !== null &&
    uvIndex <= 0;
  const isNight =
    (isMoonlightRange || isNightFromSolarAndUv) &&
    !isBeforeSunset &&
    !isSunsetWindow;
  const isCloudyNight = isNight && illuminance !== null && illuminance > 25;
  const isPartlyCloudyNight = isNight && !isMoonlightRange && !isCloudyNight;
  const isBrightDay =
    (uvIndex !== null && uvIndex >= 4) ||
    (solarRadiation !== null && solarRadiation >= 450) ||
    (illuminance !== null && illuminance >= 10000);
  const isCloudyDay =
    (uvIndex !== null && uvIndex <= 2) ||
    (solarRadiation !== null && solarRadiation < 200) ||
    (illuminance !== null && illuminance >= 25 && illuminance < 10000);

  if (lightningCount > 0) {
    return {
      condition: 'storm',
      label: 'Stormy',
      title: 'Lightning activity detected near the station',
    };
  }

  if (rainInterval > 0 || nearcastRain > 0 || precipitationType > 0) {
    return {
      condition: 'rain',
      label: 'Rain',
      title: 'Rain is currently being detected',
    };
  }

  if (isSunsetWindow) {
    const windowTitle = isCivilTwilight
      ? 'Civil twilight is active for the first 30 minutes after sunset, so the moon phase appears after twilight ends.'
      : 'Sunset is approaching, so the sunset icon appears before the dashboard switches to moon phase conditions.';
    let title = windowTitle;

    if (isCloudyDay) {
      if (windAverage >= 8) {
        title = isCivilTwilight
          ? `Post-sunset cloud cover with elevated wind. ${windowTitle}`
          : `Late-day cloud cover with elevated wind. ${windowTitle}`;
      } else {
        title = isCivilTwilight
          ? `Post-sunset light levels suggest cloud cover. ${windowTitle}`
          : `Late-day light levels suggest cloud cover. ${windowTitle}`;
      }
    } else if (isBrightDay) {
      title = isCivilTwilight
        ? `Post-sunset light remains elevated. ${windowTitle}`
        : `Sunset is approaching with elevated evening light. ${windowTitle}`;
    }

    return {
      condition: 'sunset',
      label: isCivilTwilight ? 'Civil Twilight' : 'Sunset',
      title,
    };
  }

  if (isNight) {
    if (isMoonlightRange) {
      return {
        condition: 'clear-night',
        label: 'Clear Night',
        title: 'Moonlight-level ambient light suggests a clear night sky',
      };
    }

    if (isCloudyNight) {
      return {
        condition: 'cloudy-night',
        label: 'Cloudy Night',
        title: 'Elevated nighttime ambient light suggests clouds reflecting ground light',
      };
    }

    if (isPartlyCloudyNight) {
      return {
        condition: 'partly-cloudy-night',
        label: 'Partly Cloudy Night',
        title: 'Nighttime ambient light suggests some cloud cover',
      };
    }

    return {
      condition: 'partly-cloudy-night',
      label: 'Night',
      title: 'Nighttime conditions with limited cloud-cover confidence',
    };
  }

  if (isCloudyDay) {
    if (windAverage >= 8) {
      return {
        condition: 'windy',
        label: 'Windy Clouds',
        title: 'Daytime cloud cover with elevated wind',
      };
    }

    return {
      condition: 'cloudy',
      label: 'Cloudy',
      title: 'Daytime light levels suggest cloud cover',
    };
  }

  if (isBrightDay) {
    return {
      condition: 'sunny',
      label: 'Sunny',
      title: 'Strong sunlight with no active rain',
    };
  }

  return {
    condition: 'partly-cloudy',
    label: 'Partly Cloudy',
    title: 'Mixed conditions with moderate sunlight',
  };
}

function MoonIcon() {
  const moonPhase = getMoonPhaseDetails();
  return <MoonPhaseIcon moonPhase={moonPhase} />;
}

function MoonPhaseGlyph({
  moonPhase,
  cx = 48,
  cy = 48,
  radius = 24,
  shadowClassName = 'mws-icon__moon-shadow',
  moonClassName = 'mws-icon__moon mws-icon__moon--phase',
  outlineClassName = 'mws-icon__moon-outline',
}) {
  const uniqueId = React.useId().replace(/:/g, '');
  const clampedIllumination = Math.max(0, Math.min(1, moonPhase?.illumination ?? 0));
  const shadowOffset = clampedIllumination * radius * 2;
  const shadowCx = (moonPhase?.waxing ?? true) ? cx - shadowOffset : cx + shadowOffset;
  const maskId = `mws-moon-phase-${moonPhase?.key || 'unknown'}-${uniqueId}`;

  return (
    <>
      <defs>
        <mask id={maskId}>
          <rect width="96" height="96" fill="#000" />
          <circle cx={cx} cy={cy} r={radius} fill="#fff" />
          <circle cx={shadowCx} cy={cy} r={radius} fill="#000" />
        </mask>
      </defs>
      <circle className={shadowClassName} cx={cx} cy={cy} r={radius} />
      <circle className={moonClassName} cx={cx} cy={cy} r={radius} mask={`url(#${maskId})`} />
      <circle className={outlineClassName} cx={cx} cy={cy} r={radius} />
    </>
  );
}

function MoonPhaseIcon({ moonPhase }) {
  return (
    <svg viewBox="0 0 96 96" aria-hidden="true" data-moon-phase={moonPhase?.key}>
      <MoonPhaseGlyph moonPhase={moonPhase} />
      <circle className="mws-icon__star" cx="30" cy="28" r="3.2" />
      <circle className="mws-icon__star" cx="69" cy="25" r="2.4" />
      <circle className="mws-icon__star" cx="65" cy="67" r="2.8" />
    </svg>
  );
}

function CloudyNightIcon({ moonPhase }) {
  return (
    <svg viewBox="0 0 96 96" aria-hidden="true" data-moon-phase={moonPhase?.key}>
      <circle className="mws-icon__star" cx="26" cy="27" r="2.4" />
      <circle className="mws-icon__star" cx="72" cy="26" r="2" />
      <path
        className="mws-icon__cloud mws-icon__cloud--night-bank"
        d="M17 70h25c7.7 0 13.4-5 13.4-11.9S50.2 46.7 43.2 46.7c-1.1-8.2-7.5-13.9-15.8-13.9-8 0-14.5 5.3-16 12.7C4.4 46.1 0 51.8 0 59.1 0 65.5 5.4 70 12.4 70H17Z"
      />
      <path
        className="mws-icon__cloud mws-icon__cloud--night-bank"
        d="M58 73h20.5C87.1 73 93 67.5 93 60.5S87.8 49 80.8 49c-1.1-8-7.3-13.6-15.3-13.6-7.6 0-13.9 5-15.5 12.4-6.7.7-11.8 6.2-11.8 13C38.2 67.9 43.9 73 51.3 73H58Z"
      />
      <path
        className="mws-icon__cloud mws-icon__cloud--night-bank"
        d="M29 59h38c8.2 0 14-5.3 14-12S75.4 35.7 68 35.7c-1.1-8.7-7.8-14.7-16.5-14.7-8.3 0-15.1 5.4-16.7 13.2C27.5 34.8 22 40.6 22 48 22 54.8 27 59 29 59Z"
      />
      <MoonPhaseGlyph
        moonPhase={moonPhase}
        cx={48}
        cy={39}
        radius={23}
        shadowClassName="mws-icon__moon-shadow mws-icon__moon-shadow--solid"
      />
      <path
        className="mws-icon__cloud mws-icon__cloud--night-wisp"
        d="M22 76h52c8.1 0 14-5 14-11.6S82.6 53 75.4 53c-1.1 0-2.2.1-3.2.4C69.8 48.7 65.2 46 59.6 46c-6 0-11.3 3.2-13.6 8.3-2-.9-4.2-1.4-6.7-1.4-5.9 0-10.8 3.3-12.8 8.1C18.3 61.5 13 67 13 73.2 13 75 16.4 76 22 76Z"
      />
    </svg>
  );
}

function PartlyCloudyNightIcon({ moonPhase }) {
  return (
    <svg viewBox="0 0 96 96" aria-hidden="true" data-moon-phase={moonPhase?.key}>
      <MoonPhaseGlyph
        moonPhase={moonPhase}
        shadowClassName="mws-icon__moon-shadow mws-icon__moon--back"
        moonClassName="mws-icon__moon mws-icon__moon--phase mws-icon__moon--back"
        outlineClassName="mws-icon__moon-outline mws-icon__moon--back"
      />
      <circle className="mws-icon__star" cx="24" cy="30" r="2.6" />
      <path
        className="mws-icon__cloud"
        d="M38 72h30c8.7 0 15-5.8 15-13.4S77.1 46 69.7 46c-1.2-8.7-7.8-14.7-16.3-14.7-8 0-14.8 5.4-16.4 13.1C29.5 45.1 24 50.7 24 58.1 24 65.8 30.3 72 38 72Z"
      />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg viewBox="0 0 96 96" aria-hidden="true">
      <g className="mws-icon__stroke">
        <line x1="48" y1="8" x2="48" y2="22" />
        <line x1="48" y1="74" x2="48" y2="88" />
        <line x1="8" y1="48" x2="22" y2="48" />
        <line x1="74" y1="48" x2="88" y2="48" />
        <line x1="18" y1="18" x2="28" y2="28" />
        <line x1="68" y1="68" x2="78" y2="78" />
        <line x1="18" y1="78" x2="28" y2="68" />
        <line x1="68" y1="28" x2="78" y2="18" />
      </g>
      <circle className="mws-icon__sun-core" cx="48" cy="48" r="18" />
    </svg>
  );
}

function SunsetIcon() {
  return (
    <svg viewBox="0 0 96 96" aria-hidden="true">
      <g className="mws-icon__sunset-rays">
        <line x1="48" y1="16" x2="48" y2="26" />
        <line x1="28" y1="24" x2="35" y2="31" />
        <line x1="68" y1="24" x2="61" y2="31" />
        <line x1="19" y1="43" x2="29" y2="43" />
        <line x1="67" y1="43" x2="77" y2="43" />
      </g>
      <path className="mws-icon__sunset-sun" d="M27 59a21 21 0 0 1 42 0Z" />
      <path className="mws-icon__sunset-horizon" d="M16 60h64" />
      <path className="mws-icon__sunset-line" d="M24 70h48" />
      <path className="mws-icon__sunset-line mws-icon__sunset-line--short" d="M32 78h32" />
      <path
        className="mws-icon__sunset-cloud"
        d="M52 64h20c6.2 0 10.5-4 10.5-9.3S78.4 46 73 46c-.9-6.5-5.8-10.8-12.3-10.8-6.1 0-11.2 4-12.3 9.7-5.4.5-9.4 4.8-9.4 10.6 0 5.4 4.5 8.5 10.2 8.5H52Z"
      />
    </svg>
  );
}

function CloudIcon() {
  return (
    <svg viewBox="0 0 96 96" aria-hidden="true">
      <path
        className="mws-icon__cloud"
        d="M32 70h34c10.5 0 18-7.2 18-16.5S77.4 38 68.2 38c-1.5-11.8-10.4-20-22.2-20-11.1 0-20.6 7.5-22.7 18.2C13.2 37.3 6 44.8 6 54.3 6 63.3 13.4 70 23 70h9Z"
      />
    </svg>
  );
}

function PartlyCloudyIcon() {
  return (
    <svg viewBox="0 0 96 96" aria-hidden="true">
      <g className="mws-icon__sun-backdrop">
        <line x1="30" y1="14" x2="30" y2="24" />
        <line x1="30" y1="48" x2="30" y2="58" />
        <line x1="12" y1="32" x2="22" y2="32" />
        <line x1="38" y1="32" x2="48" y2="32" />
        <line x1="17" y1="19" x2="24" y2="26" />
        <line x1="36" y1="38" x2="43" y2="45" />
      </g>
      <circle className="mws-icon__sun-core" cx="30" cy="32" r="12" />
      <path
        className="mws-icon__cloud"
        d="M34 72h34c10 0 17-6.7 17-15.5S78.5 42 69.8 42c-1.5-10.5-9.4-17.8-19.8-17.8-9.8 0-18.2 6.6-20.1 16C20.8 41 14 47.7 14 56.7 14 65 21 72 30 72h4Z"
      />
    </svg>
  );
}

function RainIcon({ rainDetails }) {
  const rainIntensity = rainDetails?.key || 'light';
  const dropletPaths = RAIN_DROP_PATHS[rainIntensity] || RAIN_DROP_PATHS.light;

  return (
    <svg viewBox="0 0 96 96" aria-hidden="true" data-rain-intensity={rainIntensity}>
      <path
        className="mws-icon__cloud"
        d="M30 58h38c10 0 17-6.7 17-15.5S78.2 28 69.6 28c-1.4-10.3-9.3-17.4-19.6-17.4-9.8 0-18.1 6.5-20 15.7C20.9 27.1 14 33.8 14 42.7 14 51 21 58 30 58Z"
      />
      <g className={`mws-icon__rain mws-icon__rain--${rainIntensity}`}>
        {dropletPaths.map((pathData) => (
          <path className="mws-icon__rain-drop" d={pathData} key={pathData} />
        ))}
      </g>
    </svg>
  );
}

function StormIcon() {
  return (
    <svg viewBox="0 0 96 96" aria-hidden="true">
      <path
        className="mws-icon__cloud"
        d="M30 54h40c10 0 17-6.8 17-15.6S80.2 24 71.4 24c-1.5-10.7-9.5-18.1-20.4-18.1-10 0-18.5 6.7-20.5 16.3C21 23.3 14 30.3 14 39.4 14 47.8 21 54 30 54Z"
      />
      <path className="mws-icon__bolt" d="M48 56 38 74h11l-4 16 15-22H49l4-12Z" />
    </svg>
  );
}

function WindyCloudIcon() {
  return (
    <svg viewBox="0 0 96 96" aria-hidden="true">
      <path
        className="mws-icon__cloud"
        d="M30 52h38c10 0 17-6.5 17-15.1S78.3 22.8 69.6 22.8c-1.4-10-9.3-16.8-19.6-16.8-9.7 0-18 6.3-20 15.2C21 22.1 14 28.6 14 37.2 14 45.2 21 52 30 52Z"
      />
      <g className="mws-icon__wind">
        <path d="M18 66h38c5 0 7-2.5 7-5 0-3-2.3-5-5.2-5-2.4 0-4.3 1.1-5.2 3" />
        <path d="M10 76h54c5 0 8-2.6 8-5.8 0-3.3-2.8-5.8-6-5.8-2.7 0-4.9 1.2-5.9 3.3" />
      </g>
    </svg>
  );
}

function getWeatherIcon(condition) {
  switch (condition) {
    case 'storm':
      return StormIcon;
    case 'sunny':
      return SunIcon;
    case 'sunset':
      return SunsetIcon;
    case 'clear-night':
      return MoonIcon;
    case 'cloudy-night':
      return CloudyNightIcon;
    case 'partly-cloudy-night':
      return PartlyCloudyNightIcon;
    case 'cloudy':
      return CloudIcon;
    case 'windy':
      return WindyCloudIcon;
    default:
      return PartlyCloudyIcon;
  }
}

function MeshtasticWeatherIconSummary({ weatherData, timestamp }) {
  const summary = resolveWeatherSummary(weatherData, timestamp);
  const moonPhase = getMoonPhaseDetails(timestamp ?? weatherData?.timestamp);
  const iconLabel = summary.condition.includes('night')
    ? `Weather summary: ${summary.label}, ${moonPhase.label}`
    : `Weather summary: ${summary.label}`;
  const iconTitle = summary.condition.includes('night')
    ? `${summary.title} Current moon phase: ${moonPhase.label}.`
    : summary.title;
  let iconMarkup;

  if (summary.condition === 'clear-night') {
    iconMarkup = <MoonPhaseIcon moonPhase={moonPhase} />;
  } else if (summary.condition === 'cloudy-night') {
    iconMarkup = <CloudyNightIcon moonPhase={moonPhase} />;
  } else if (summary.condition === 'partly-cloudy-night') {
    iconMarkup = <PartlyCloudyNightIcon moonPhase={moonPhase} />;
  } else if (summary.condition === 'rain') {
    iconMarkup = <RainIcon rainDetails={getRainIntensityDetails(weatherData)} />;
  } else {
    const Icon = getWeatherIcon(summary.condition);
    iconMarkup = <Icon />;
  }

  return (
    <div
      className={`mws-hero-icon mws-hero-icon--${summary.condition}`}
      role="img"
      aria-label={iconLabel}
      title={iconTitle}
    >
      {iconMarkup}
      <span className="mws-visually-hidden">{summary.label}</span>
    </div>
  );
}

export default MeshtasticWeatherIconSummary;
